import { type IMediaInstance, sound } from '@pixi/sound';
import { VolumeStorage } from '@/utils/VolumeStorage';

/**
 * Sound categories for organization and volume control
 */
export enum SoundCategory {
  MUSIC = 'music',
  SFX = 'sfx',
  VOICE = 'voice',
  AMBIENT = 'ambient',
}

/**
 * Sound configuration interface
 */
export interface SoundConfig {
  src: string;
  category: SoundCategory;
  volume?: number;
  loop?: boolean;
  autoPlay?: boolean;
  preload?: boolean;
}

/**
 * Sound manager for handling game audio using PixiJS Sound
 */
export class SoundManager {
  private static instance: SoundManager;

  // Track loaded sounds
  private loadedSounds: Map<string, boolean> = new Map();

  // Category volumes with default values
  private volumes: Record<SoundCategory, number> = {
    [SoundCategory.MUSIC]: 0.1,
    [SoundCategory.SFX]: 0.7,
    [SoundCategory.VOICE]: 1.0,
    [SoundCategory.AMBIENT]: 0.3,
  };

  // Master volume
  private masterVolume: number = 1.0;

  // Currently playing music track
  private currentMusicTrack: string | null = null;

  // Mute state - start muted by default
  private muted: boolean = true;

  // Audio context initialization state
  private audioContextInitialized: boolean = false;

  /**
   * Private constructor for singleton pattern
   */
  private constructor() {
    // Initialize sound system with some basic settings
    // But don't start AudioContext until user interaction
    sound.disableAutoPause = true; // Continue playing when page loses focus

    // Load saved volume settings from local storage
    this.loadVolumeSettings();
  }

  /**
   * Load volume settings from local storage
   */
  private loadVolumeSettings(): void {
    // Load master volume
    this.masterVolume = VolumeStorage.loadMasterVolume(this.masterVolume);

    // Load category volumes
    Object.keys(this.volumes).forEach(category => {
      const typedCategory = category as SoundCategory;
      this.volumes[typedCategory] = VolumeStorage.loadCategoryVolume(
        typedCategory,
        this.volumes[typedCategory]
      );
    });

    // Override any saved mute state - we always start muted now
    this.muted = true;

    console.log('Loaded volume settings (starting muted):', {
      masterVolume: this.masterVolume,
      volumes: this.volumes,
      muted: this.muted
    });
  }

  /**
   * Resume or initialize the AudioContext
   * When skipResume is true, it will only create the context without resuming it
   * @param skipResume Whether to skip resuming the context (for initial load)
   */
  public async resumeAudioContext(skipResume: boolean = false): Promise<void> {
    try {
      // Wait a moment for any browser initialization to complete
      await new Promise(resolve => setTimeout(resolve, 200));

      // First make sure we have a sound context object
      if (!sound.context) {
        // Try with normal initialization but don't start (important for auto-start)
        // @ts-ignore - Accessing internal method
        sound.initializeMediaContext(false); // false = don't start automatically
        console.log('Attempted to initialize media context without auto-start');
      }

      // If still no context, try direct creation
      if (!sound.context) {
        try {
          // @ts-ignore - Create context directly
          sound.context = {};
          // Create AudioContext with proper type checking
          // Define a type for the AudioContext constructor
          type AudioContextConstructor = new () => AudioContext;
          // Use a proper type assertion to handle PIXI sound's internal structure
          const AudioContextClass = (window.AudioContext ||
            (window as unknown as { webkitAudioContext?: AudioContextConstructor })
              .webkitAudioContext) as AudioContextConstructor;
          // @ts-ignore - Internal PIXI Sound structure access
          sound.context.ctx = new AudioContextClass();
          console.log('Manually created AudioContext');
        } catch (e) {
          console.error('Failed to manually create AudioContext:', e);
        }
      }

      // Get the audio context
      const context = this.getAudioContext();

      // If still no context, try one last approach with a timeout
      if (!context) {
        console.warn('AudioContext still not available, trying with delay...');
        await new Promise(resolve => setTimeout(resolve, 500));

        // Try direct Web Audio API
        try {
          // Create a temporary context just to see if we can
          // Define a type for the AudioContext constructor
          type AudioContextConstructor = new () => AudioContext;
          // Get the appropriate AudioContext class
          const AudioContextClass = (window.AudioContext ||
            (window as unknown as { webkitAudioContext?: AudioContextConstructor })
              .webkitAudioContext) as AudioContextConstructor;
          const tempContext = new AudioContextClass();
          console.log('Temporary AudioContext created successfully:', tempContext.state);

          // If we got here, Web Audio API works but PIXI sound may have issues
          // Let's try to hook it up to PIXI sound
          if (!sound.context) {
            // @ts-ignore - Create context directly
            sound.context = {};
          }
          // @ts-ignore - Assign our working context
          sound.context.ctx = tempContext;
          console.log('Assigned working context to PIXI sound');
        } catch (e) {
          console.error('Failed final AudioContext creation attempt:', e);
          return Promise.reject(
            'AudioContext not available - browser may not support Web Audio API',
          );
        }
      }

      // Get the context again after our attempts
      const finalContext = this.getAudioContext();

      if (!finalContext) {
        console.error('AudioContext could not be created or accessed after multiple attempts');
        return Promise.reject('AudioContext not available');
      }

      // Resume the context if not skipping the resume operation
      if (!skipResume && finalContext.state === 'suspended') {
        try {
          await finalContext.resume();
          console.log('AudioContext resumed successfully');
        } catch (e) {
          console.warn('Could not resume AudioContext, will need user interaction:', e);
          // Don't throw, we'll try again after user interaction
        }
      } else if (skipResume && finalContext.state === 'suspended') {
        console.log('AudioContext created but left in suspended state (waiting for user interaction)');
      }

      // Only try to verify running state if we're not skipping resume
      if (!skipResume && (finalContext.state === 'suspended' || finalContext.state === 'closed')) {
        console.warn(`AudioContext is in ${finalContext.state} state, trying to force activation`);

        // Create a simple oscillator to force activation
        const oscillator = finalContext.createOscillator();
        const gainNode = finalContext.createGain();
        gainNode.gain.value = 0; // Silent
        oscillator.connect(gainNode);
        gainNode.connect(finalContext.destination);
        oscillator.start(0);
        oscillator.stop(0.001); // Extremely short duration

        // Wait to see if this helped
        await new Promise(resolve => setTimeout(resolve, 100));

        if (finalContext.state === 'suspended' || finalContext.state === 'closed') {
          console.error(
            `AudioContext still in ${finalContext.state} state after force activation attempt`,
          );
          return Promise.reject('Could not activate AudioContext');
        }
      }

      this.audioContextInitialized = true;
      console.log('AudioContext initialized successfully, state:', finalContext.state);
      return Promise.resolve();
    } catch (error) {
      console.error('Error resuming AudioContext:', error);
      return Promise.reject(error);
    }
  }

  /**
   * Helper method to access the internal Web Audio API context
   */
  private getAudioContext(): AudioContext | null {
    try {
      // Try to access the internal context
      // Need to access PIXI Sound's internal AudioContext
      // @ts-ignore - Accessing internal PIXI Sound structure
      const ctx = sound.context?.ctx || null;
      return ctx;
    } catch (e) {
      console.warn('Could not access AudioContext:', e);
      return null;
    }
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  /**
   * Add a sound to the library
   */
  public addSound(id: string, config: SoundConfig): void {
    // Don't re-add sounds that are already loaded
    if (this.loadedSounds.has(id)) return;

    sound.add(id, {
      url: config.src,
      preload: config.preload ?? false,
      loop: config.loop ?? false,
      volume: this.calculateVolume(config.category, config.volume),
      autoPlay: config.autoPlay ?? false,
    });

    this.loadedSounds.set(id, true);
  }

  /**
   * Preload multiple sounds at once using a safer approach
   */
  public preloadSounds(soundConfigs: Record<string, SoundConfig>): Promise<unknown[]> {
    const loadPromises: Promise<unknown>[] = [];

    console.log(`Attempting to preload sounds:`, Object.keys(soundConfigs).join(', '));

    // Instead of adding all sounds at once, add and load them sequentially
    // This helps prevent AudioBufferSourceNode buffer conflicts
    for (const [id, config] of Object.entries(soundConfigs)) {
      try {
        // Skip if already loaded
        if (this.loadedSounds.has(id) && sound.exists(id)) {
          const existingInstance = sound.find(id);
          if (existingInstance?.isLoaded) {
            console.log(`Sound ${id} already loaded, skipping`);
            continue;
          }
        }

        // Ensure old instances are properly removed before adding
        if (sound.exists(id)) {
          // Remove old instance to avoid buffer conflicts
          try {
            console.log(`Removing existing sound ${id} before reloading`);
            sound.remove(id);
            this.loadedSounds.delete(id); // Reset loaded state
          } catch (e) {
            console.warn(`Error removing existing sound ${id}:`, e);
          }
        }

        // Add the sound to the library without preload
        console.log(`Adding sound ${id} with source ${config.src}`);
        sound.add(id, {
          url: config.src,
          preload: false, // We'll handle preloading manually
          loop: config.loop ?? false,
          volume: this.calculateVolume(config.category, config.volume),
          autoPlay: false, // Never autoplay during preload
        });

        this.loadedSounds.set(id, false); // Mark as added but not fully loaded

        // Create a promise for loading this sound
        const loadPromise = new Promise<void>(resolve => {
          // Set a maximum timeout to avoid hanging indefinitely
          const maxWaitTime = 10000; // 10 seconds max wait

          // Create an array to store any references we need to clean up
          const cleanupRefs: ReturnType<typeof setTimeout>[] = [];

          // Safely load the sound using fetch to avoid AudioContext issues
          const handleLoad = () => {
            // Use fetch API to manually get the audio file
            fetch(config.src)
              .then(response => {
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                return response.blob(); // Just download the file, we don't need the buffer
              })
              .then(() => {
                // Now the file is downloaded, update the sound instance
                try {
                  // Check if the sound still exists (it might have been removed)
                  if (sound.exists(id)) {
                    // Just mark as loaded without directly setting buffer
                    this.loadedSounds.set(id, true);
                    console.log(`Sound ${id} loaded successfully via fetch`);
                  } else {
                    console.warn(`Sound ${id} no longer exists after fetch`);
                  }
                } catch (e) {
                  console.error(`Error processing ${id} after fetch:`, e);
                }

                // Always resolve so we don't block other sounds
                resolve();
              })
              .catch(error => {
                console.error(`Fetch error for sound ${id}:`, error);
                this.loadedSounds.set(id, false);
                resolve(); // Still resolve to not block other sounds
              });
          };

          // Start loading after a small delay to avoid overwhelming the audio system
          setTimeout(handleLoad, 50);

          // Also set a safety timeout
          const timeoutId = setTimeout(() => {
            console.warn(`Sound ${id} load timed out after ${maxWaitTime}ms, continuing anyway`);
            resolve();
          }, maxWaitTime);

          cleanupRefs.push(timeoutId);
        });

        loadPromises.push(loadPromise);
      } catch (e) {
        console.error(`Error setting up load for sound ${id}:`, e);
      }
    }

    // If there are no promises, return immediately
    if (loadPromises.length === 0) {
      console.log('No sounds needed loading, returning immediately');
      return Promise.resolve([]);
    }

    return Promise.all(loadPromises);
  }

  /**
   * Check if AudioContext is initialized
   */
  public isAudioContextInitialized(): boolean {
    return this.audioContextInitialized;
  }

  /**
   * Play a sound with buffer error protection
   */
  public play(
    id: string,
    options?: {
      loop?: boolean;
      volume?: number;
      speed?: number;
    },
  ): IMediaInstance | null {
    if (!sound || !sound.exists(id)) {
      console.warn(`Sound "${id}" not found`);
      return null;
    }

    try {
      // First check if the sound is currently playing
      const currentlyPlaying = this.isPlaying(id);

      // If it's already playing and we try to play it again, this can cause the buffer error
      // So we need to stop it first
      if (currentlyPlaying) {
        sound.stop(id);
      }

      // If this is a music track, manage music state
      // REMOVED: Don't automatically treat all looping sounds as music
      // usage of play() should be for SFX only, use playMusic() for BGM
      /*
      if (sound.find(id)?.loop) {
        if (this.currentMusicTrack && this.currentMusicTrack !== id) {
          this.stopMusic();
        }
        this.currentMusicTrack = id;
      }
      */

      // Apply options before playing if instance exists
      const instance = sound.find(id);
      if (options && instance) {
        if (options.loop !== undefined) instance.loop = options.loop;
        if (options.volume !== undefined) instance.volume = this.muted ? 0 : options.volume;
        if (options.speed !== undefined) instance.speed = options.speed;
      }

      // Give a small delay if we just stopped it to allow buffer cleanup
      if (currentlyPlaying) {
        // Return null as we'll play it after a delay
        setTimeout(() => {
          try {
            const delayedInstance = sound.play(id);
            if (
              delayedInstance &&
              typeof delayedInstance === 'object' &&
              !('then' in delayedInstance)
            ) {
              // Apply options again to the new instance
              if (options) {
                if (options.loop !== undefined) delayedInstance.loop = options.loop;
                if (options.volume !== undefined)
                  delayedInstance.volume = this.muted ? 0 : options.volume;
                if (options.speed !== undefined) delayedInstance.speed = options.speed;
              }
            }
          } catch (e) {
            console.error(`Error playing sound ${id} after delay:`, e);
          }
        }, 50);
        return null;
      }

      // Play the sound if not previously playing
      const playResult = sound.play(id);

      // Handle potential promise (though we expect an instance)
      if (playResult && typeof playResult === 'object' && 'then' in playResult) {
        console.warn(`Sound ${id} returned a Promise instead of an instance, ignoring`);
        return null;
      }

      return playResult || null;
    } catch (e) {
      console.error(`Error playing sound ${id}:`, e);
      return null;
    }
  }

  /**
   * Play a music track with crossfade
   */
  public playMusic(
    id: string,
    fadeTime: number = 1000,
  ): IMediaInstance | Promise<IMediaInstance> | null {
    if (!sound.exists(id)) {
      console.warn(`Music track "${id}" not found`);
      return null;
    }

    // Don't restart the same track if it's already playing
    if (this.currentMusicTrack === id && this.isPlaying(id)) {
      console.log(`Music track ${id} is already playing, skipping restart`);
      return null;
    }

    const oldTrack = this.currentMusicTrack;
    this.currentMusicTrack = id;

    // Fade out the current track if one is playing
    if (oldTrack && this.isPlaying(oldTrack)) {
      const oldInstance = sound.find(oldTrack);
      if (oldInstance) {
        // Fade out current track
        const startVolume = oldInstance.volume;
        let elapsed = 0;
        const fadeInterval = 50; // ms

        const fadeOutInterval = setInterval(() => {
          elapsed += fadeInterval;
          const ratio = Math.max(0, 1 - elapsed / fadeTime);
          oldInstance.volume = startVolume * ratio;

          if (elapsed >= fadeTime) {
            clearInterval(fadeOutInterval);
            oldInstance.stop();
          }
        }, fadeInterval);
      }
    }

    // Start the new track
    const newInstance = sound.find(id);
    if (newInstance) {
      newInstance.loop = true;

      if (fadeTime > 0) {
        // Start with volume 0 and fade in
        newInstance.volume = 0;
        sound.play(id);

        let elapsed = 0;
        const fadeInterval = 50; // ms
        const targetVolume = this.calculateVolume(SoundCategory.MUSIC);

        const fadeInInterval = setInterval(() => {
          elapsed += fadeInterval;
          const ratio = Math.min(1, elapsed / fadeTime);
          newInstance.volume = this.muted ? 0 : targetVolume * ratio;

          if (elapsed >= fadeTime) {
            clearInterval(fadeInInterval);
          }
        }, fadeInterval);
      } else {
        // Play at normal volume immediately
        newInstance.volume = this.calculateVolume(SoundCategory.MUSIC);
        sound.play(id);
      }

      return sound.play(id) || null;
    }

    return null;
  }

  /**
   * Stop a sound
   */
  public stop(id: string): void {
    if (sound.exists(id)) {
      sound.stop(id);

      // Clear current music track if applicable
      if (this.currentMusicTrack === id) {
        this.currentMusicTrack = null;
      }
    }
  }

  /**
   * Stop all currently playing music
   */
  public stopMusic(): void {
    if (this.currentMusicTrack && sound.exists(this.currentMusicTrack)) {
      sound.stop(this.currentMusicTrack);
      this.currentMusicTrack = null;
    }
  }

  /**
   * Stop all sounds
   */
  public stopAll(): void {
    sound.stopAll();
    this.currentMusicTrack = null;
  }

  /**
   * Pause a sound
   */
  public pause(id: string): void {
    if (sound.exists(id)) {
      sound.pause(id);
    }
  }

  /**
   * Resume a paused sound
   */
  public resume(id: string): void {
    if (sound.exists(id)) {
      sound.resume(id);
    }
  }

  /**
   * Set the volume for a specific sound
   */
  public setVolume(id: string, volume: number): void {
    if (sound.exists(id)) {
      const instance = sound.find(id);
      if (instance) {
        instance.volume = this.muted ? 0 : Math.max(0, Math.min(1, volume));
      }
    }
  }

  /**
   * Set the volume for a category
   */
  public setCategoryVolume(category: SoundCategory, volume: number): void {
    this.volumes[category] = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();

    // Save to local storage
    VolumeStorage.saveCategoryVolume(category, this.volumes[category]);
  }

  /**
   * Set the master volume
   */
  public setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();

    // Save to local storage
    VolumeStorage.saveMasterVolume(this.masterVolume);
  }

  /**
   * Toggle mute state
   */
  public toggleMute(): boolean {
    this.muted = !this.muted;
    this.updateAllVolumes();

    // Save to local storage
    VolumeStorage.saveMuteState(this.muted);

    return this.muted;
  }

  /**
   * Set mute state
   */
  public setMute(muted: boolean): void {
    if (this.muted !== muted) {
      this.muted = muted;
      this.updateAllVolumes();

      // Save to local storage
      VolumeStorage.saveMuteState(this.muted);
    }
  }

  /**
   * Check if sound is muted
   */
  public isMuted(): boolean {
    return this.muted;
  }

  /**
   * Get the current master volume
   */
  public getMasterVolume(): number {
    return this.masterVolume;
  }

  /**
   * Get the current volume for a category
   */
  public getCategoryVolume(category: SoundCategory): number {
    return this.volumes[category];
  }

  /**
   * Check if a sound is currently playing
   */
  public isPlaying(id: string): boolean {
    const instance = sound.find(id);
    return !!instance && instance.isPlaying === true;
  }

  /**
   * Calculate the actual volume for a sound based on its category and optional individual volume
   */
  private calculateVolume(category: SoundCategory, individualVolume: number = 1.0): number {
    if (this.muted) return 0;
    const categoryVolume = this.volumes[category] || 1.0;
    return this.masterVolume * categoryVolume * individualVolume;
  }

  /**
   * Update volumes for all currently playing sounds
   */
  private updateAllVolumes(): void {
    for (const id of this.loadedSounds.keys()) {
      if (sound.exists(id)) {
        const instance = sound.find(id);
        if (instance) {
          // Try to determine the category from the instance
          let category = SoundCategory.SFX; // default

          if (id === this.currentMusicTrack) {
            category = SoundCategory.MUSIC;
          }
          // Update volume based on category and master settings
          instance.volume = this.muted ? 0 : this.calculateVolume(category);
        }
      }
    }
  }

  /**
   * Destroy the sound manager and clean up resources
   */
  public destroy(): void {
    this.stopAll();
    for (const id of this.loadedSounds.keys()) {
      if (sound.exists(id)) {
        sound.remove(id);
      }
    }
    this.loadedSounds.clear();
  }
}
